import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgbModalRef, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounce } from 'lodash-es';
import { Observable } from 'rxjs';

import { DashpivotEvent, EventNotifierService, EventTypes } from '@site-mate/dashpivot-shared-library';

import { TemplateSelection } from 'app/apps/template-selection.model';
import { PreviewFormComponent } from 'app/form/edit-form/preview-form/preview-form.component';
import { SegmentService } from 'app/segment/segment.service';
import { TemplateWeb } from 'app/shared/model/template.model';

import { TemplateSelectorUtils } from '../template-selector-util';

@Component({
  selector: 'cc-templates-selector-register-view',
  templateUrl: 'templates-selector-register-view.component.html',
  styleUrls: ['templates-selector-register-view.component.scss'],
})
export class TemplatesSelectorRegisterViewComponent implements OnChanges, OnInit {
  public modalReference: NgbModalRef;
  hideBottomBorder = true;
  allTemplatesSelected = false;

  @Input() visibleTemplates: TemplateWeb[];
  @Input() isLibrarySelection: boolean;
  @Input() isSingleSelectionOnly: boolean;
  @Input() selectedTemplates: TemplateWeb[];
  @Input() onSelectionChange: Observable<void>;

  @Output() onSelect = new EventEmitter<TemplateSelection>();
  @ViewChild('tableBody') tbody: ElementRef;

  debouncedUpdateBorderOnScrollEnd: () => void;

  constructor(private readonly modal: NgbModal, private readonly segmentService: SegmentService) {
    this.debouncedUpdateBorderOnScrollEnd = debounce(this.updateBorderOnScrollEnd, 30);
  }

  ngOnInit(): void {
    this.onSelectionChange.subscribe(() => {
      this.allTemplatesSelected = this.areAllTemplatesSelected();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.visibleTemplates) {
      this.debouncedUpdateBorderOnScrollEnd();
    }

    this.allTemplatesSelected = this.areAllTemplatesSelected();
  }

  onSelectAllTemplates(event) {
    this.allTemplatesSelected = event.target.checked;
    this.onSelect.emit({ templateId: 'All', isSelected: event.target.checked });
  }

  onTemplateSelection(template?: TemplateWeb) {
    this.allTemplatesSelected = this.areAllTemplatesSelected();
    this.onSelect.emit({ templateId: template._id, isSelected: template.selected });
  }

  onRowSelection(template) {
    template.selected = !template.selected;
    this.onTemplateSelection(template);
  }

  preventPropagationOnClick(event) {
    event.stopPropagation();
  }

  onPreviewClick(template) {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.TemplatePreviewOpened, { Context: template?.uniqueAppId }),
      this.segmentService,
    );

    this.modalReference = this.modal.open(PreviewFormComponent, {
      backdrop: 'static',
      windowClass: 'form-modal full-modal',
    });

    this.modalReference.componentInstance.templateId = template.id;
    this.modalReference.componentInstance.isPreview = true;
    this.modalReference.componentInstance.isCompanyLibrary = !this.isLibrarySelection;
    this.modalReference.componentInstance.counterIsActivated = template.counterIsActivated;
    this.modalReference.componentInstance.templateUniqueAppId = template.uniqueAppId;
    this.modalReference.componentInstance.templateName = template.name;
    this.modalReference.componentInstance.templateOrientation = template.orientation;

    this.modalReference.result
      .then(() => {
        template.selected = true;
        this.onTemplateSelection(template);
        return true;
      })
      .catch(() => {
        // no-op
      });
  }

  private areAllTemplatesSelected() {
    return this.visibleTemplates.every((template) => template.selected);
  }

  private updateBorderOnScrollEnd() {
    this.hideBottomBorder = !TemplateSelectorUtils.canKeepScrolling(this.tbody);
  }
}
